import { useEffect, useMemo, useState } from 'react'
import {
  Form,
  message,
  Select,
  Spin,
  Table,
} from 'antd'
import {
  PlusCircleFilled,
  EditFilled,
  DeleteOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { Head, usePage } from '@inertiajs/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import EventLayout from '@/components/layouts/EventLayout'
import { PageHeader } from '@/components/patterns'
import {
  Button,
  Drawer,
  ConfirmModal,
  TableSearch,
  TableActions,
  Avatar,
  StatusIndicator,
  Badge,
  Pagination,
  SearchFilterModal,
  type FilterCondition,
  type FilterableField,
} from '@/components/ui'
import type { TableAction } from '@/components/ui'
import { api } from '@/lib/apiClient'
import { keys } from '@/lib/queryKeys'
import EventUserForm, {
  EventUserFormValues,
} from './components/EventUserForm'
import UserSearchStep, {
  type UserSearchResult,
} from './components/UserSearchStep'

type EventSummary = {
  id: number
  name: string
  slug: string
  location?: string
}

export type AssignedEventUser = {
  id: number
  user_id: number
  name: string
  email: string
  mobile?: string
  designation?: string
  status: 'active' | 'inactive'
  assigned_role: 'owner' | 'crew'
  created_at: string
  updated_at: string
}

type PageProps = {
  event: EventSummary
  assignedUsers: AssignedEventUser[]
  roleOptions: string[]
  statusOptions: string[]
}

type EventUserRequestBody = {
  user: {
    name: string
    email: string
    mobile?: string
    designation?: string
    status: string
  }
  event_assignment: {
    role: string
  }
}

type MutationResponse = {
  success: boolean
  user?: AssignedEventUser
  errors?: string[]
  /** True when the email already had an account (from another event) that was assigned here */
  linked_existing_account?: boolean
}

const toRequestBody = (values: EventUserFormValues): EventUserRequestBody => {
  const { event_role, ...user } = values
  return {
    user,
    event_assignment: {
      role: event_role,
    },
  }
}

const getErrorMessage = (error: any, fallback: string) => {
  if (error?.data && Array.isArray(error.data.errors)) {
    return `${fallback}: ${error.data.errors.join(', ')}`
  }
  if (Array.isArray(error?.errors)) {
    return `${fallback}: ${error.errors.join(', ')}`
  }
  return fallback
}

const PAGE_SIZE = 50

/** Matches MIN_SEARCH_LENGTH in Events::UserManagement::UsersController */
const MIN_USER_SEARCH_LENGTH = 2
const USER_SEARCH_DEBOUNCE_MS = 300

/** Steps of the Add User drawer: look the person up, then assign or create */
type AddUserStep = 'search' | 'assign' | 'create'

type UserSearchResponse = {
  query: string
  results: UserSearchResult[]
}

const EventUserManagementPage = () => {
  const { event, assignedUsers = [], roleOptions = [], statusOptions = [] } =
    usePage<PageProps>().props
  const slug = event.slug
  const queryClient = useQueryClient()

  const [searchText, setSearchText] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false)
  const [addUserStep, setAddUserStep] = useState<AddUserStep>('search')
  const [userSearch, setUserSearch] = useState('')
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('')
  const [selectedExistingUser, setSelectedExistingUser] =
    useState<UserSearchResult | null>(null)
  const [existingUserRole, setExistingUserRole] = useState<string>(
    roleOptions[0] || 'owner'
  )
  const [editDrawerOpen, setEditDrawerOpen] = useState(false)
  const [currentAssignment, setCurrentAssignment] =
    useState<AssignedEventUser | null>(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [assignmentToDelete, setAssignmentToDelete] =
    useState<AssignedEventUser | null>(null)
  const [filterModalOpen, setFilterModalOpen] = useState(false)
  const [filterConditions, setFilterConditions] = useState<FilterCondition[]>([])
  const [filterNameSearch, setFilterNameSearch] = useState('')

  // Filter fields for SearchFilterModal
  const filterFields: FilterableField[] = [
    {
      id: 'assigned_role',
      identifier: 'assigned_role',
      label: 'Assigned Role',
      type: 'select',
      options: roleOptions.map(r => ({
        label: r === 'owner' ? 'Event Owner' : 'Crew User',
        value: r,
      })),
    },
    {
      id: 'status',
      identifier: 'status',
      label: 'Status',
      type: 'select',
      options: statusOptions.map(s => ({ label: s, value: s })),
    },
  ]

  const [createForm] = Form.useForm<EventUserFormValues>()
  const [editForm] = Form.useForm<EventUserFormValues>()

  const usersQuery = useQuery({
    queryKey: keys.eventUserManagement.list(slug),
    queryFn: () =>
      api.get<AssignedEventUser[]>(
        `/events/${slug}/user_management/users`
      ),
    initialData: assignedUsers,
    initialDataUpdatedAt: Date.now(),
  })

  useEffect(() => {
    if (createDrawerOpen) {
      createForm.setFieldsValue({
        status: statusOptions[0] || 'active',
        event_role: roleOptions[0] || 'owner',
      })
    } else {
      createForm.resetFields()
      setAddUserStep('search')
      setUserSearch('')
      setDebouncedUserSearch('')
      setSelectedExistingUser(null)
      setExistingUserRole(roleOptions[0] || 'owner')
    }
  }, [createDrawerOpen, createForm, roleOptions, statusOptions])

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedUserSearch(userSearch.trim()),
      USER_SEARCH_DEBOUNCE_MS
    )
    return () => clearTimeout(timer)
  }, [userSearch])

  const userSearchQuery = useQuery({
    queryKey: keys.eventUserManagement.search(slug, debouncedUserSearch),
    queryFn: () =>
      api.get<UserSearchResponse>(
        `/events/${slug}/user_management/users/search?q=${encodeURIComponent(debouncedUserSearch)}`
      ),
    enabled:
      createDrawerOpen &&
      addUserStep === 'search' &&
      debouncedUserSearch.length >= MIN_USER_SEARCH_LENGTH,
  })

  useEffect(() => {
    if (editDrawerOpen && currentAssignment) {
      editForm.setFieldsValue({
        name: currentAssignment.name,
        email: currentAssignment.email,
        mobile: currentAssignment.mobile,
        designation: currentAssignment.designation,
        status: currentAssignment.status,
        event_role: currentAssignment.assigned_role,
      })
    } else if (!editDrawerOpen) {
      editForm.resetFields()
    }
  }, [editDrawerOpen, currentAssignment, editForm])

  const refetchUsers = () =>
    queryClient.invalidateQueries({
      queryKey: keys.eventUserManagement.list(slug),
    })

  const createUserMutation = useMutation({
    mutationFn: (values: EventUserFormValues) =>
      api.post<MutationResponse, EventUserRequestBody>(
        `/events/${slug}/user_management/users`,
        toRequestBody(values)
      ),
    onSuccess: (response) => {
      message.success(
        response.linked_existing_account
          ? 'Existing account added to this event'
          : 'User added successfully'
      )
      refetchUsers()
      setCreateDrawerOpen(false)
    },
    onError: (error) => {
      message.error(getErrorMessage(error, 'Failed to add user'))
    },
  })

  const updateUserMutation = useMutation({
    mutationFn: (values: EventUserFormValues) => {
      if (!currentAssignment) {
        throw new Error('No assignment selected for update')
      }
      return api.patch<MutationResponse, EventUserRequestBody>(
        `/events/${slug}/user_management/users/${currentAssignment.id}`,
        toRequestBody(values)
      )
    },
    onSuccess: () => {
      message.success('User updated successfully')
      refetchUsers()
      setEditDrawerOpen(false)
      setCurrentAssignment(null)
    },
    onError: (error: any) => {
      message.error(getErrorMessage(error, 'Failed to update user'))
    },
  })

  const deleteUserMutation = useMutation({
    mutationFn: (assignmentId: number) =>
      api.delete<{ success: boolean }>(
        `/events/${slug}/user_management/users/${assignmentId}`
      ),
    onSuccess: () => {
      message.success('User removed from event')
      refetchUsers()
      setDeleteModalOpen(false)
      setAssignmentToDelete(null)
    },
    onError: (error: any) => {
      message.error(getErrorMessage(error, 'Failed to remove user'))
    },
  })

  const handleCreateSubmit = () => {
    createForm
      .validateFields()
      .then((values) => createUserMutation.mutate(values))
      .catch(() => null)
  }

  const handleSelectExistingUser = (user: UserSearchResult) => {
    setSelectedExistingUser(user)
    setExistingUserRole(roleOptions[0] || 'owner')
    setAddUserStep('assign')
  }

  // Carry the search text over so the operator does not retype it: an email
  // query fills the email field, anything else fills the name.
  const handleCreateNewUser = () => {
    const query = userSearch.trim()
    createForm.setFieldsValue({
      email: query.includes('@') ? query : undefined,
      name: query.includes('@') ? undefined : query,
      status: (statusOptions[0] || 'active') as EventUserFormValues['status'],
      event_role: (roleOptions[0] || 'owner') as EventUserFormValues['event_role'],
    })
    setAddUserStep('create')
  }

  // The create endpoint assigns an account that already exists for this email,
  // so adding a found person is the same request with only the email filled in.
  const handleAssignExistingUser = () => {
    if (!selectedExistingUser) return

    createUserMutation.mutate({
      name: selectedExistingUser.name,
      email: selectedExistingUser.email,
      mobile: selectedExistingUser.mobile,
      designation: selectedExistingUser.designation,
      status: selectedExistingUser.status,
      event_role: existingUserRole as EventUserFormValues['event_role'],
    })
  }

  const handleEditSubmit = () => {
    editForm
      .validateFields()
      .then((values) => updateUserMutation.mutate(values))
      .catch(() => null)
  }

  const handleEdit = (assignment: AssignedEventUser) => {
    setCurrentAssignment(assignment)
    setEditDrawerOpen(true)
  }

  const handleDelete = (assignment: AssignedEventUser) => {
    setAssignmentToDelete(assignment)
    setDeleteModalOpen(true)
  }

  const filteredUsers = useMemo(() => {
    const needle = appliedSearch.trim().toLowerCase()
    return (usersQuery.data || []).filter((user) => {
      // Search filter
      const haystack = [
        user.name,
        user.email,
        user.mobile,
        user.designation,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

      const matchesSearch = !needle || haystack.includes(needle)

      // Apply filter conditions
      const matchesConditions = filterConditions.every((condition) => {
        if (!condition.field || !condition.value) return true
        const fieldValue = user[condition.field as keyof AssignedEventUser]
        if (condition.operator === 'is') {
          return fieldValue === condition.value
        }
        if (condition.operator === 'contains') {
          return String(fieldValue).toLowerCase().includes(condition.value.toLowerCase())
        }
        if (condition.operator === 'not_equals') {
          return fieldValue !== condition.value
        }
        return true
      })

      return matchesSearch && matchesConditions
    })
  }, [appliedSearch, usersQuery.data, filterConditions])

  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    return filteredUsers.slice(startIndex, startIndex + pageSize)
  }, [filteredUsers, currentPage, pageSize])

  const handleSearch = () => {
    setAppliedSearch(searchText)
    setCurrentPage(1)
  }

  const handleSearchReset = () => {
    setSearchText('')
    setAppliedSearch('')
    setFilterConditions([])
    setCurrentPage(1)
  }

  const handleApplyFilters = () => {
    // Apply name search from filter modal
    if (filterNameSearch) {
      setAppliedSearch(filterNameSearch)
      setSearchText(filterNameSearch)
    }
    setCurrentPage(1)
    setFilterModalOpen(false)
  }

  const handleResetFilters = () => {
    setFilterNameSearch('')
    setFilterConditions([])
    setAppliedSearch('')
    setSearchText('')
    setCurrentPage(1)
    setFilterModalOpen(false)
  }

  const handlePageChange = (page: number, newPageSize: number) => {
    setCurrentPage(page)
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize)
      setCurrentPage(1)
    }
  }

  const columns: ColumnsType<AssignedEventUser> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text: string) => (
        <div className="flex items-center gap-3">
          <Avatar name={text} size="small" />
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
    {
      title: 'Mobile Number',
      dataIndex: 'mobile',
      key: 'mobile',
      sorter: (a, b) => (a.mobile || '').localeCompare(b.mobile || ''),
      render: (value?: string) => value || '—',
    },
    {
      title: 'Assigned Role',
      dataIndex: 'assigned_role',
      key: 'assigned_role',
      sorter: (a, b) => a.assigned_role.localeCompare(b.assigned_role),
      render: (role: string) => {
        // Can have multiple roles - handle as array or single value
        const roles = Array.isArray(role) ? role : [role]
        return (
          <div className="flex flex-wrap gap-1">
            {roles.map((r, index) => (
              <Badge
                key={index}
                variant="outline"
              >
                {r === 'owner' ? 'Event Owner' : 'Crew User'}
              </Badge>
            ))}
          </div>
        )
      },
    },
    {
      title: 'Designation',
      dataIndex: 'designation',
      key: 'designation',
      sorter: (a, b) => (a.designation || '').localeCompare(b.designation || ''),
      render: (value?: string) => value || '—',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: (status: string) => (
        <StatusIndicator
          status={status === 'active' ? 'success' : 'inactive'}
          label={status}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      sorter: false,
      render: (_: any, record: AssignedEventUser) => {
        const actions: TableAction[] = [
          {
            key: 'edit',
            icon: <EditFilled />,
            onClick: () => handleEdit(record),
            ariaLabel: 'Edit user',
          },
          {
            key: 'delete',
            icon: <DeleteOutlined />,
            onClick: () => handleDelete(record),
            ariaLabel: 'Delete user',
            danger: true,
          },
        ]

        return <TableActions actions={actions} />
      },
    },
  ]

  const tableContent = () => {
    if (usersQuery.isLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <Spin size="large" />
        </div>
      )
    }

    if (usersQuery.isError) {
      return (
        <div className="text-center text-red-500">
          Unable to load users. Please try again.
        </div>
      )
    }

    return (
      <>
        <Table
          columns={columns}
          dataSource={(paginatedUsers || []).map((user) => ({
            ...user,
            key: user.id,
          }))}
          pagination={false}
          loading={usersQuery.isFetching}
          rowKey="id"
          showSorterTooltip={false}
          className="ava-data-table"
          size="middle"
        />
        {filteredUsers.length > 0 && (
          <div className="mt-4">
            <Pagination
              current={currentPage}
              total={filteredUsers.length}
              pageSize={pageSize}
              onChange={handlePageChange}
            />
          </div>
        )}
      </>
    )
  }

  return (
    <EventLayout>
      <Head title={`${event.name} - User Management`} />
      <PageHeader
        title="User Management"
        subtitle="Create users and assign roles to help manage your events."
        headerAction={
          <Button
            variant="primary"
            icon={<PlusCircleFilled />}
            onClick={() => setCreateDrawerOpen(true)}
          >
            Add New User
          </Button>
        }
      />

      <section className="px-6 py-6 bg-[#f3f5f8] min-h-[500px]">
        <div className="p-6">
          {/* Search Toolbar */}
          <div className="mb-4">
            <TableSearch
              value={searchText}
              onChange={setSearchText}
              onSearch={handleSearch}
              onReset={handleSearchReset}
              onFilter={() => setFilterModalOpen(true)}
              placeholder="Search Name or Email"
              showFilter={true}
            />
          </div>

          {/* Table */}
          {tableContent()}
        </div>
      </section>

      <Drawer
        title={addUserStep === 'create' ? 'Create New User' : 'Add User'}
        open={createDrawerOpen}
        onClose={() => setCreateDrawerOpen(false)}
        showFooter={addUserStep !== 'search'}
        customFooter={
          addUserStep === 'search' ? null : (
            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                size="small"
                onClick={() => setAddUserStep('search')}
                disabled={createUserMutation.isPending}
              >
                Back
              </Button>
              <Button
                variant="primary"
                size="small"
                onClick={
                  addUserStep === 'assign'
                    ? handleAssignExistingUser
                    : handleCreateSubmit
                }
                loading={createUserMutation.isPending}
              >
                {addUserStep === 'assign' ? 'Add to Event' : 'Add User'}
              </Button>
            </div>
          )
        }
      >
        {addUserStep === 'search' && (
          <UserSearchStep
            query={userSearch}
            onQueryChange={setUserSearch}
            results={userSearchQuery.data?.results || []}
            searching={userSearchQuery.isFetching}
            searched={userSearchQuery.isSuccess}
            minQueryLength={MIN_USER_SEARCH_LENGTH}
            onSelect={handleSelectExistingUser}
            onCreateNew={handleCreateNewUser}
          />
        )}

        {addUserStep === 'assign' && selectedExistingUser && (
          <div>
            <div className="flex items-center gap-3 px-3 py-3 bg-[#F3F5F8] border border-[#EBEBEB]">
              <Avatar name={selectedExistingUser.name} size="small" />
              <div className="min-w-0">
                <div className="text-sm text-black/85 truncate">
                  {selectedExistingUser.name}
                </div>
                <div className="text-xs text-black/45 truncate">
                  {selectedExistingUser.email}
                </div>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assigned Role
              </label>
              <Select
                value={existingUserRole}
                onChange={setExistingUserRole}
                options={roleOptions.map((role) => ({
                  value: role,
                  label: role === 'owner' ? 'Event Owner' : 'Crew User',
                }))}
                style={{ width: '100%' }}
                size="small"
              />
            </div>

            <p className="mt-3 text-xs text-black/45">
              This person keeps their existing account and profile — only their role on this event
              is set here.
            </p>
          </div>
        )}

        {addUserStep === 'create' && (
          <EventUserForm
            form={createForm}
            roleOptions={roleOptions}
            statusOptions={statusOptions}
          />
        )}
      </Drawer>

      <Drawer
        title="Edit User"
        open={editDrawerOpen}
        onClose={() => {
          setEditDrawerOpen(false)
          setCurrentAssignment(null)
        }}
        onSubmit={handleEditSubmit}
        submitText="Save Changes"
        loading={updateUserMutation.isPending}
        showCancelButton={false}
      >
        <EventUserForm
          form={editForm}
          roleOptions={roleOptions}
          statusOptions={statusOptions}
        />
      </Drawer>

      <ConfirmModal
        title="Remove User"
        open={deleteModalOpen}
        onConfirm={() => {
          if (assignmentToDelete) {
            deleteUserMutation.mutate(assignmentToDelete.id)
          }
        }}
        onClose={() => {
          setDeleteModalOpen(false)
          setAssignmentToDelete(null)
        }}
        confirmText="Delete"
        danger
        loading={deleteUserMutation.isPending}
      >
        <div>
          <p className="mb-3">
            Are you sure you want to remove{' '}
            <strong>{assignmentToDelete?.name}</strong> from{' '}
            <strong>{event.name}</strong>?
          </p>
          <p className="text-sm text-gray-500">
            This action only removes the user from this event. Their account
            remains available for future assignments.
          </p>
        </div>
      </ConfirmModal>

      <SearchFilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        title="Filter Users"
        description="Show users that match the following..."
        fields={filterFields}
        filterConditions={filterConditions}
        onFilterConditionsChange={setFilterConditions}
        showNameSearch={true}
        nameSearchPlaceholder="Search by name or email"
        nameSearchValue={filterNameSearch}
        onNameSearchChange={setFilterNameSearch}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />
    </EventLayout>
  )
}

export default EventUserManagementPage
